import { Observable } from 'rxjs';
import { RoleNames } from './role';
import * as moment from 'moment';

export enum PermissionType {
    category,
    permission,
    namespace
}

export class PermissionStatus {
    permission: IPermissionDto;
    hasPermission: boolean
}

export class User implements IUser {

    public get roles() { return this.userContext.roles.map(r => r.value as RoleNames); }
    public get id() { return this.userContext.user.objectId; }
    public get userName() { return this.userContext.user.userPrincipalName; }
    public get email() { return this.userContext.user.userPrincipalName; }
    public get firstName() { return this.userContext.user.firstName; }
    public get lastName() { return this.userContext.user.lastName; }
    public get fullName() { return `${this.userContext.user.firstName} ${this.userContext.user.lastName}`; }
    public get uniqueName() { return this.userContext.user.userPrincipalName; };
    public get objectId() { return this.userContext.user.objectId; }
    public get displayName() { return this.userContext.user.displayName; }


    photo: User;
    userContext: IUserContext;

    constructor(input?: Partial<IUser>) {
        Object.assign(this, input);
    }

    userInitials(): string {
        return (
            this.firstName.charAt(0).toUpperCase() +
            this.lastName.charAt(0).toUpperCase()
        );
    }
}

export interface IUser {
    userName: string;
    firstName: string;
    lastName: string;
    email: string;
    fullName: string;
    roles: RoleNames[];
    displayName: string;
}

export interface IUserList {
    id: string;
    name: string;
    email: string
}

export interface ProfileResponse {
    businessPhones: any[];
    displayName: string;
    givenName: string;
    id: string;
    jobTitle: string;
    mail: string;
    mobilePhone: string;
    officeLocation: string;
    preferredLanguage: string;
    surname: string;
    userPrincipalName: string;
    photo: any;
}

export interface IUserContext {
    user: IUserDto;
    roles: IRoleDto[];
    permissions: IPermissionDto[];
    manager: IUserDto;
}

export interface IPermissionDto {
    id: number;
    name: string;
    displayName: string;
    category: string;
    ttl: number;
    createdOn: string;
    modifiedOn: string;
    metadata?: {
        viewId?: number;
        [key: string]: any;
    }


    userPermissionId: number;
    rolePermissions: IRolePermissionDto[];
    $currentPosition: 'SOURCE' | 'TARGET';
    $status: 'ADDED';
}

export interface IUserDto {

    id: number;
    objectId: string;
    firstName: string;
    lastName: string;
    userPrincipalName: string;
    accountEnabled: boolean;
    metadata: any;
    createdOn: Date;
    modifiedOn: Date;
    displayName: string;
    validFrom: Date;
    validTo: Date;
    managerObjectId?: string;
    TeamLeadObjectId?: string;
    jobTitle?: string;
    lock?: boolean;

    readonly fullName: string;
    readonly lastNameFirstName: string;

    readonly formattedDisplay: string;

    readonly accountDisabled: boolean;

    userPermissionId: number;
    $currentPosition: string;
    rolePermissions: IRolePermissionDto[];
    $status: 'ADDED';
}


export class UserDto implements IUserDto {
    accountEnabled: boolean;
    createdOn: Date;
    displayName: string;
    firstName: string;
    id: number;
    lastName: string;
    metadata: any;
    modifiedOn: Date;
    objectId: string;
    userPrincipalName: string;

    validFrom: Date;
    validTo: Date;
    managerObjectId?: string;
    teamLeadObjectId?: string;
    jobTitle?: string;

    userPermissionId: number;
    $currentPosition: string;
    rolePermissions: IRolePermissionDto[];

    readonly fullName: string;
    readonly lastNameFirstName: string;

    readonly formattedDisplay: string;


    $status: 'ADDED';
    readonly accountDisabled: boolean;

    constructor(input: Partial<IUserDto>) {
        Object.assign(this, input);

        this.fullName = this.buildFullName();
        this.lastNameFirstName = this.buildLastNameFirstName();
        this.formattedDisplay = this.buildFormattedDisplay();
        this.accountDisabled = !this.accountEnabled;
    }

    private buildFullName() {
        return `${this.firstName} ${this.lastName}`;
    }

    private buildLastNameFirstName() {
        return `${this.lastName}, ${this.firstName}`;
    }

    private buildFormattedDisplay() {
        return this.lastNameFirstName;
    }
}

export interface IRolePermissionDto {
    role: IRoleDto;
    permission: IPermissionDto;
}

export interface IRoleDto {
    id?: number;
    objectId: string;
    description: string;
    displayName: string;
    isEnabled: boolean;
    createdOn: string;
    modifiedOn: string;
    value: string;

    userPermissionId: number;
}

export interface IPermissionType {
    selectionName: string;
    permissionType: PermissionType;
}


export interface PermissionStatusDto {
    permission: IPermissionDto;
    hasPermission: boolean;
}

export interface IEffectivePermissions {
    [key: string]: Observable<boolean>;
}

export interface IEffectivePermissionCache extends PermissionStatusDto {
    ttlExpirationDate?: Date;
    lastRegistrationDate: Date;
    registrationCount: number;
    readonly hasExpiredTTL: boolean;
}

export class EffectivePermissionCache implements IEffectivePermissionCache {
    permission: IPermissionDto;
    hasPermission: boolean;
    ttlExpirationDate?: Date;
    lastRegistrationDate: Date;
    registrationCount: number;

    get hasExpiredTTL() {
        return this.ttlExpirationDate < moment().toDate();
    }
    constructor(input: Partial<IEffectivePermissionCache>) {
        Object.assign(this, input);

        if (!!this.ttlExpirationDate) {
            this.ttlExpirationDate = moment(this.ttlExpirationDate).toDate();
        }

        if (!!this.lastRegistrationDate) {
            this.lastRegistrationDate = moment(this.lastRegistrationDate).toDate();
        }
    }

}

export interface IEffectivePermissionTracking {
    name: string;
    lastAccessTracking: Date;
}

export interface IAddUserRoleRelationResponse {
    added: string[];
}


export interface IAddPermissionRelationResponse {
    added: number[];
}
export interface IAddUserDtoResponse {

}

export interface IAddRoleDtoResponse {

}

export enum PermissionListDisplayMode {
    Flat = 'FLAT',
    Category = 'CATEGORY',
    Namespace = 'NAMESPACE'
}

export interface IAddUserTenantRelationResponse {
    added: string[];
}

export interface IUserConnection {
    id: number;
    entityId: number;
    entityType: string;
    entityTenantId: string;
    memberName: string;
    connectionType: string;
    permission: string;
    qualifier: string;
}

export interface IUserConnectionGetRequest {
    userObjectId: string;
}

export interface IUserConnectionGetResponse {
    statusCode: number;
    connectionNodes: IUserConnection[];
    assignableUsers: IAssignableUser[];
}

export interface IUserConnectionReassignRequest {
    assignedUserObjectId: string;
    assignedUserDisplayName: string;
    userConnectionIds: number[];
}

export interface IUserConnectionReassignResponse {
    statusCode: number;
}

export interface IAssignableUser {
    userObjectId: string;
    displayName: string;
    permissions: string[];
}

export interface IPhoneInfo {
    phoneNumber?: string;
    phoneExtension: string;
}
